import * as Sentry from '@sentry/react';

import { ApiError, type AppError, normalizeError, sanitizeDiagnosticValue } from './app-error';

export interface ErrorReportContext {
  componentStack?: string | null;
  operation?: string;
  metadata?: Record<string, unknown>;
}

const reportedErrors = new WeakMap<object, string>();

const asSentryContext = (value: unknown): Record<string, unknown> => {
  const sanitized = sanitizeDiagnosticValue(value);
  return sanitized && typeof sanitized === 'object' && !Array.isArray(sanitized)
    ? (sanitized as Record<string, unknown>)
    : { value: sanitized };
};

/**
 * Sends one enriched event to Sentry and returns its event id. Re-reporting the
 * same Error instance is deduplicated, which prevents an interceptor and an
 * error boundary from creating two events for one failure.
 */
export const reportError = (
  error: unknown,
  context: ErrorReportContext = {},
  options: { force?: boolean } = {}
): { error: AppError; eventId?: string } => {
  const normalized = normalizeError(error);
  if (!options.force && !normalized.reportable) return { error: normalized };

  const dedupeTarget = error && typeof error === 'object' ? error : normalized;
  const priorEventId = reportedErrors.get(dedupeTarget);
  if (priorEventId) return { error: normalized, eventId: priorEventId };

  let eventId: string | undefined;
  Sentry.withScope((scope) => {
    scope.setLevel(normalized.severity);
    scope.setTag('app.error_code', normalized.code);
    if (normalized.route) scope.setTag('app.route', normalized.route);
    if (context.operation) scope.setTag('app.operation', context.operation);
    scope.setContext('app_error', {
      code: normalized.code,
      occurred_at: normalized.occurredAt,
      route: normalized.route,
      user_message: normalized.userMessage,
    });

    if (normalized instanceof ApiError) {
      if (normalized.status) scope.setTag('http.status_code', String(normalized.status));
      if (normalized.requestId) scope.setTag('server.request_id', normalized.requestId);
      if (normalized.backendReportId) {
        scope.setTag('server.report_id', normalized.backendReportId);
      }
      if (normalized.request?.method) {
        scope.setTag('http.request.method', normalized.request.method);
      }
      if (normalized.request?.url) scope.setTag('http.url', normalized.request.url);
      scope.setFingerprint([
        'api-error',
        normalized.code,
        normalized.request?.method ?? 'unknown-method',
        normalized.request?.url ?? 'unknown-url',
      ]);
      if (normalized.request) scope.setContext('api_request', asSentryContext(normalized.request));
      if (normalized.response)
        scope.setContext('api_response', asSentryContext(normalized.response));
    }
    if (context.componentStack) {
      scope.setContext('react', { component_stack: context.componentStack });
    }
    if (context.metadata) scope.setContext('metadata', asSentryContext(context.metadata));

    eventId = Sentry.captureException(normalized);
  });

  if (eventId) {
    normalized.reportId = eventId;
    reportedErrors.set(normalized, eventId);
    reportedErrors.set(dedupeTarget, eventId);
  }
  return { error: normalized, eventId };
};

/** Associates subsequent events with an internal user id without sending email or other PII. */
export const setErrorReportingUser = (userId: string | number | null | undefined): void => {
  Sentry.setUser(userId === null || userId === undefined ? null : { id: String(userId) });
};
